'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { validateMinorBirthdate, type BirthdateRejection } from '@/utils';

type AgeVerificationErrorCode = BirthdateRejection | 'SAVE_FAILED';

interface AgeVerificationModalProps {
  isOpen: boolean;
  /** Return value is ignored — handlers may be sync or async. */
  onSubmit: (isAdult: boolean, birthdate?: string | null) => void | Promise<unknown>;
  /**
   * When provided, renders a close affordance (X button + backdrop click).
   * Omit it for a blocking modal the member must answer — see AgeVerificationGate.
   */
  onClose?: () => void;
  saving?: boolean;
  /** Error reported by the server; local validation errors are held internally. */
  errorCode?: AgeVerificationErrorCode | null;
}

/**
 * The two answers the radiogroup offers, named for the `ageGroup` values the
 * signup form posts so the two age fields stay recognisably the same control.
 * `null` is "not answered yet" — the state Continue refuses to submit from.
 */
type AgeGroupChoice = 'under_18' | 'age_18_plus' | null;

const ERROR_ID = 'age-verification-error';
const AGE_GROUP_ERROR_ID = 'age-verification-age-group-error';
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AgeVerificationModal({
  isOpen,
  onSubmit,
  onClose,
  saving = false,
  errorCode = null,
}: AgeVerificationModalProps) {
  const { t } = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock background scroll while open — the modal covers the viewport and blocks
  // app use until answered.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Focus trap. This modal is blocking, so keyboard and assistive-technology
  // users must not be able to Tab into the app behind the overlay — visual
  // stacking alone does not keep a control out of reach.
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const getFocusable = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    const initial = getFocusable();
    (initial[0] ?? panel).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const inPanel = active instanceof Node && panel.contains(active);

      if (!inPanel) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const copy = t.modals.ageVerification;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-verification-title"
      data-testid="age-verification-modal-root"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose ? () => onClose() : undefined}
        data-testid="age-verification-backdrop"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative bg-background rounded-2xl shadow-xl w-full max-w-md mx-auto p-6 sm:p-8 max-h-[90vh] overflow-y-auto outline-none"
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-foreground-muted hover:text-foreground"
            aria-label={copy.closeLabel}
            data-testid="age-verification-close-btn"
          >
            &times;
          </button>
        )}

        <h2
          id="age-verification-title"
          className="font-serif text-2xl sm:text-3xl text-primary mb-3"
        >
          {copy.title}
        </h2>
        <p className="text-sm md:text-base text-foreground mb-6">{copy.lead}</p>

        <AgeVerificationForm onSubmit={onSubmit} saving={saving} errorCode={errorCode} />
      </div>
    </div>
  );
}

interface AgeVerificationFormProps {
  onSubmit: AgeVerificationModalProps['onSubmit'];
  saving: boolean;
  errorCode: AgeVerificationErrorCode | null;
}

/**
 * The answer itself, split out so it owns the transient state.
 *
 * The gate keeps the modal permanently mounted and only toggles `isOpen`, so
 * anything held above this boundary would survive a close and resurface on
 * re-open — a stale error or half-typed birthdate over what should be a fresh
 * question. This component is rendered only while the modal is open, so closing
 * unmounts it and discards that state outright. Structural, rather than an
 * effect that has to remember to tidy up.
 */
function AgeVerificationForm({ onSubmit, saving, errorCode }: AgeVerificationFormProps) {
  const { t } = useLanguage();
  const [ageGroup, setAgeGroup] = useState<AgeGroupChoice>(null);
  const [birthdate, setBirthdate] = useState('');
  const [localError, setLocalError] = useState<BirthdateRejection | null>(null);
  // Mirrors the signup form: the "pick one" complaint stays hidden until the
  // member actually tries to continue, rather than scolding them on open.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const copy = t.modals.ageVerification;
  const isMinor = ageGroup === 'under_18';
  const isAgeGroupSelected = ageGroup !== null;

  // Local validation wins while the member is correcting the field; the server
  // code is what remains once a submitted answer comes back refused.
  const shownError: AgeVerificationErrorCode | null = localError ?? errorCode;
  const errorMessage = shownError ? copy.errors[shownError] : null;
  const ageGroupMissing = submitAttempted && !isAgeGroupSelected;

  const handleSelectMinor = () => {
    if (saving) return;
    setLocalError(null);
    setAgeGroup('under_18');
  };

  const handleSelectAdult = () => {
    if (saving) return;
    setLocalError(null);
    setAgeGroup('age_18_plus');
    // Drop any date typed before they switched — matches the signup form, and
    // keeps a stale minor birthdate from riding along with an adult answer.
    setBirthdate('');
  };

  const handleContinue = () => {
    if (saving) return;
    setSubmitAttempted(true);

    // Nothing chosen yet: the radiogroup shows its own complaint. Returning here
    // rather than disabling the button keeps the control operable, so the member
    // is told what is missing instead of meeting a dead button.
    if (!isAgeGroupSelected) return;

    if (!isMinor) {
      setLocalError(null);
      void onSubmit(true, null);
      return;
    }

    // Validate rather than silently no-op, for the same reason.
    const rejection = validateMinorBirthdate(birthdate);
    if (rejection) {
      setLocalError(rejection);
      return;
    }
    setLocalError(null);
    void onSubmit(false, birthdate);
  };

  return (
    <>
      {/* Age - radio buttons. Mirrors the signup form's age field (AuthModal) so
          a member meets the same control, wording and markup in both places. */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {t.auth.age} <span className="text-red-500">*</span>
        </label>
        <div
          role="radiogroup"
          aria-label={t.auth.age}
          aria-invalid={ageGroupMissing}
          aria-describedby={ageGroupMissing ? AGE_GROUP_ERROR_ID : undefined}
          className={`space-y-2 ${ageGroupMissing ? 'p-2 border border-red-500 rounded-md' : ''}`}
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="ageVerificationAgeGroup"
              checked={ageGroup === 'under_18'}
              onChange={handleSelectMinor}
              disabled={saving}
              className="w-4 h-4 border-border text-primary focus:ring-primary"
              data-testid="age-verification-age-under-18"
            />
            <span className="text-sm text-foreground">{t.auth.ageConfirmation13to15}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="ageVerificationAgeGroup"
              checked={ageGroup === 'age_18_plus'}
              onChange={handleSelectAdult}
              disabled={saving}
              className="w-4 h-4 border-border text-primary focus:ring-primary"
              data-testid="age-verification-age-18-plus"
            />
            <span className="text-sm text-foreground">{t.auth.ageConfirmation16Plus}</span>
          </label>
        </div>
        {ageGroupMissing && (
          <p
            id={AGE_GROUP_ERROR_ID}
            className="text-xs text-red-500 mt-1"
            role="alert"
            data-testid="age-verification-age-group-error"
          >
            {t.errors.ageGroupRequired}
          </p>
        )}
      </div>

      {/* Birthdate - only for 13-17, exactly as on signup. */}
      {isMinor && (
        <div className="mt-4">
          <label
            htmlFor="age-verification-birthdate"
            className="block text-sm font-medium text-foreground mb-1"
          >
            {t.auth.birthdate}
          </label>
          <input
            id="age-verification-birthdate"
            type="date"
            value={birthdate}
            onChange={(e) => {
              setBirthdate(e.target.value);
              // Clear the stale complaint as soon as they start correcting it.
              if (localError) setLocalError(null);
            }}
            disabled={saving}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={errorMessage ? ERROR_ID : 'age-verification-birthdate-helper'}
            className="w-full px-3 py-2.5 border border-border rounded-md text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            data-testid="age-verification-birthdate"
          />
          {!errorMessage && (
            <p id="age-verification-birthdate-helper" className="text-xs text-caption mt-1">
              {t.auth.birthdateHelper}
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={handleContinue}
        disabled={saving}
        aria-describedby={errorMessage ? ERROR_ID : undefined}
        className="w-full mt-5 px-8 py-3 bg-primary text-white rounded-full text-sm font-semibold tracking-wider uppercase hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="age-verification-submit-btn"
      >
        {saving ? copy.saving : copy.continueBtn}
      </button>

      {errorMessage && (
        <p
          id={ERROR_ID}
          className="text-sm text-red-600 mt-4"
          role="alert"
          data-testid="age-verification-error"
        >
          {errorMessage}
        </p>
      )}
    </>
  );
}
